import * as React from "react";
import { CalendarHeart, Loader2, PartyPopper } from "lucide-react";
import { ApiError } from "@eduviaos/shared";
import { Button, Input, Label, Textarea, cn } from "@eduviaos/ui-kit";
import { Reveal, SectionCard, SectionHeader } from "../../components/Section";
import {
  LEAVE_TYPE_LABELS,
  istToday,
  parseDay,
  shortDay,
  useCancelLeave,
  useMyHolidays,
  useMyLeaveBalance,
  useMyLeaves,
  useRequestLeave,
  type LeaveStatus,
  type LeaveType,
  type MyLeave,
} from "../../lib/team/api";

/** Phase SC-22d — leave balance, requests and the centre's holidays. */

const STATUS_COPY: Record<LeaveStatus, { label: string; tone: string }> = {
  pending: {
    label: "Waiting",
    tone: "bg-amber-100 text-amber-900 dark:bg-amber-900/30 dark:text-amber-200",
  },
  approved: { label: "Approved", tone: "bg-primary/12 text-primary" },
  rejected: { label: "Not approved", tone: "bg-destructive/10 text-destructive" },
  cancelled: { label: "Cancelled", tone: "bg-muted text-muted-foreground" },
};

function range(l: { start_date: string; end_date: string; half_day: boolean }): string {
  if (l.start_date === l.end_date) {
    return `${shortDay(l.start_date)}${l.half_day ? " (half day)" : ""}`;
  }
  return `${shortDay(l.start_date)} – ${shortDay(l.end_date)}`;
}

function LeaveForm({ onDone }: { onDone: () => void }) {
  const today = istToday();
  const [type, setType] = React.useState<LeaveType>("casual");
  const [start, setStart] = React.useState(today);
  const [end, setEnd] = React.useState(today);
  const [half, setHalf] = React.useState(false);
  const [reason, setReason] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const request = useRequestLeave();

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await request.mutateAsync({
        leave_type: type,
        start_date: start,
        end_date: half ? start : end,
        half_day: half,
        reason: reason.trim() || null,
      });
      onDone();
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : "Couldn't send the request. Check your connection.",
      );
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3" aria-label="Ask for leave">
      {error ? (
        <p role="alert" className="bg-destructive/10 text-destructive rounded-xl px-3 py-2 text-sm">
          {error}
        </p>
      ) : null}
      <div className="flex flex-wrap gap-1.5" role="radiogroup" aria-label="Leave type">
        {(Object.keys(LEAVE_TYPE_LABELS) as LeaveType[]).map((t) => (
          <button
            key={t}
            type="button"
            role="radio"
            aria-checked={type === t}
            onClick={() => setType(t)}
            className={cn(
              "h-9 rounded-full border px-3.5 text-sm transition-[background-color,border-color,color,transform] duration-150 ease-out active:scale-[0.97]",
              type === t
                ? "border-primary bg-primary text-primary-foreground"
                : "border-border text-muted-foreground",
            )}
          >
            {LEAVE_TYPE_LABELS[t]}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label htmlFor="tl-start" className="text-xs">
            From
          </Label>
          <Input
            id="tl-start"
            type="date"
            className="mt-1 h-11"
            value={start}
            onChange={(e) => {
              setStart(e.target.value);
              if (e.target.value > end) setEnd(e.target.value);
            }}
          />
        </div>
        <div>
          <Label htmlFor="tl-end" className="text-xs">
            To
          </Label>
          <Input
            id="tl-end"
            type="date"
            className="mt-1 h-11"
            value={half ? start : end}
            min={start}
            disabled={half}
            onChange={(e) => setEnd(e.target.value)}
          />
        </div>
      </div>
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          className="accent-primary size-4"
          checked={half}
          onChange={(e) => setHalf(e.target.checked)}
        />
        Half day
      </label>
      <div>
        <Label htmlFor="tl-reason" className="text-xs">
          Reason (optional)
        </Label>
        <Textarea
          id="tl-reason"
          rows={2}
          maxLength={1000}
          className="mt-1"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
        />
      </div>
      <div className="flex gap-2">
        <Button
          type="submit"
          className="h-11 flex-1 rounded-2xl active:scale-[0.97]"
          disabled={request.isPending || !start}
        >
          {request.isPending ? (
            <Loader2 aria-hidden="true" className="size-4 animate-spin" />
          ) : null}
          Send request
        </Button>
        <Button type="button" variant="ghost" className="h-11 rounded-2xl" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

function LeaveRow({ leave }: { leave: MyLeave }) {
  const cancel = useCancelLeave();
  const status = STATUS_COPY[leave.status];
  return (
    <li className="flex items-center justify-between gap-3 px-4 py-3">
      <div className="min-w-0">
        <p className="text-sm font-medium">
          {LEAVE_TYPE_LABELS[leave.leave_type]} · {range(leave)}
        </p>
        <p className="text-muted-foreground truncate text-xs">
          {leave.days_requested} day{leave.days_requested === 1 ? "" : "s"}
          {leave.status === "approved" && leave.unpaid_days > 0
            ? ` · ${leave.unpaid_days} unpaid`
            : ""}
          {leave.decision_note ? ` · ${leave.decision_note}` : ""}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <span className={cn("rounded-full px-2.5 py-0.5 text-[0.7rem] font-medium", status.tone)}>
          {status.label}
        </span>
        {leave.status === "pending" ? (
          <Button
            size="sm"
            variant="ghost"
            disabled={cancel.isPending}
            onClick={() => cancel.mutate(leave.id)}
            aria-label={`Cancel leave ${range(leave)}`}
          >
            Cancel
          </Button>
        ) : null}
      </div>
    </li>
  );
}

export function TeamLeaves({ allowRequests }: { allowRequests: boolean }) {
  const [asking, setAsking] = React.useState(false);
  const leaves = useMyLeaves();
  const balance = useMyLeaveBalance();
  const items = leaves.data?.items ?? [];

  return (
    <div className="space-y-5">
      <Reveal>
        <SectionHeader title="Leave" level={1} />
        <SectionCard className="space-y-4">
          <div className="grid grid-cols-3 gap-2 text-center">
            {(
              [
                ["Left", balance.data?.available_days],
                ["Used", balance.data?.used_paid_days],
                ["Waiting", balance.data?.pending_days],
              ] as const
            ).map(([label, value]) => (
              <div key={label} className="bg-muted/50 rounded-xl px-1 py-2">
                <p className="font-display text-lg font-semibold tabular-nums">{value ?? "—"}</p>
                <p className="text-muted-foreground text-[0.7rem]">{label} paid days</p>
              </div>
            ))}
          </div>
          {allowRequests ? (
            asking ? (
              <LeaveForm onDone={() => setAsking(false)} />
            ) : (
              <Button
                className="h-11 w-full rounded-2xl active:scale-[0.97]"
                onClick={() => setAsking(true)}
                data-testid="ask-for-leave"
              >
                <CalendarHeart aria-hidden="true" className="size-4" />
                Ask for leave
              </Button>
            )
          ) : (
            <p className="text-muted-foreground text-sm">Ask your centre directly for leave.</p>
          )}
        </SectionCard>
      </Reveal>

      <Reveal index={1}>
        <SectionHeader title="Your requests" />
        {leaves.isError ? (
          <SectionCard role="alert" className="flex items-center justify-between gap-3 text-sm">
            <span className="text-destructive">Couldn&apos;t load your requests.</span>
            <Button size="sm" variant="outline" onClick={() => leaves.refetch()}>
              Retry
            </Button>
          </SectionCard>
        ) : items.length === 0 ? (
          <SectionCard className="text-muted-foreground text-sm">
            {leaves.isLoading ? "Loading…" : "No leave requests yet."}
          </SectionCard>
        ) : (
          <SectionCard className="p-0">
            <ul className="divide-border/60 divide-y">
              {items.map((l) => (
                <LeaveRow key={l.id} leave={l} />
              ))}
            </ul>
          </SectionCard>
        )}
      </Reveal>
    </div>
  );
}

export function TeamHolidays() {
  const today = istToday();
  const [y] = parseDay(today);
  const holidays = useMyHolidays(today, `${y + 1}-12-31`);
  const items = holidays.data?.items ?? [];

  return (
    <div className="space-y-5">
      <Reveal>
        <SectionHeader title="Holidays" level={1} />
        {holidays.isError ? (
          <SectionCard role="alert" className="flex items-center justify-between gap-3 text-sm">
            <span className="text-destructive">Couldn&apos;t load holidays.</span>
            <Button size="sm" variant="outline" onClick={() => holidays.refetch()}>
              Retry
            </Button>
          </SectionCard>
        ) : items.length === 0 ? (
          <SectionCard className="flex flex-col items-center gap-3 py-9 text-center">
            <span className="bg-muted text-muted-foreground grid size-14 place-items-center rounded-2xl">
              <PartyPopper aria-hidden="true" className="size-7" />
            </span>
            <p className="text-muted-foreground text-sm">
              {holidays.isLoading ? "Loading…" : "No upcoming holidays on the calendar."}
            </p>
          </SectionCard>
        ) : (
          <SectionCard className="p-0">
            <ul className="divide-border/60 divide-y">
              {items.map((h) => (
                <li
                  key={`${h.start_date}-${h.name}`}
                  className="flex items-center justify-between gap-3 px-4 py-3"
                >
                  <p className="text-sm font-medium">{h.name}</p>
                  <span className="text-muted-foreground shrink-0 text-xs">
                    {range({ start_date: h.start_date, end_date: h.end_date, half_day: false })}
                  </span>
                </li>
              ))}
            </ul>
          </SectionCard>
        )}
      </Reveal>
    </div>
  );
}
